/**
 * Hook for managing voice mode voice preferences
 */

import type { RealtimeVoice } from "@/types/user-settings"

import { useUpdateSettingsSection, useUserSettings } from "./use-user-settings"

/**
 * Available realtime voices. Display strings live under `settings.voice_mode_voices`
 * in the locale files; resolve `nameKey`/`descriptionKey` with `t()` at render time.
 */
export const REALTIME_VOICES = [
  {
    id: "cedar",
    nameKey: "settings.voice_mode_voices.cedar.name",
    descriptionKey: "settings.voice_mode_voices.cedar.description",
  },
  {
    id: "marin",
    nameKey: "settings.voice_mode_voices.marin.name",
    descriptionKey: "settings.voice_mode_voices.marin.description",
  },
] as const

export function isRealtimeVoice(value: unknown): value is RealtimeVoice {
  return typeof value === "string" && REALTIME_VOICES.some((voice) => voice.id === value)
}

export function useVoiceSettings() {
  const { data: userSettings } = useUserSettings()
  const { mutate: updateSection } = useUpdateSettingsSection()

  // OpenAI recommends Cedar or Marin for the highest Realtime voice quality.
  // Retired saved choices normalize to Cedar so the client and server agree.
  const savedVoice = userSettings?.data.voice.selected_voice
  const selectedVoice: RealtimeVoice = isRealtimeVoice(savedVoice) ? savedVoice : "cedar"

  const setSelectedVoice = (voice: RealtimeVoice) => {
    updateSection({ section: "voice", data: { selected_voice: voice } })
  }

  return { selectedVoice, setSelectedVoice, voices: REALTIME_VOICES }
}
